import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Api } from '../../../smoothr-web-app-core/api/api';
import { Gender } from 'src/smoothr-web-app-core/enums/Gender';
import { TranslateService } from '@ngx-translate/core';

import Venue from 'src/smoothr-web-app-core/models/Venue';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationService } from 'src/app/services/navigation.service';
import { IonInput, ModalController } from '@ionic/angular';
import { Location } from '@angular/common';
import Customer from 'src/smoothr-web-app-core/models/Customer';
import { InfoModalComponent } from 'src/app/components/info-modal/info-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatBirthDateForApi, getBirthDateParts, getPhoneFormValue, isPhoneFormValueValid } from 'src/app/utils/customer-profile.utils';

@Component({
	selector: 'app-user-data',
	templateUrl: './user-data.page.html',
	styleUrls: ['user-data.page.scss'],
})
export class UserDataPage implements OnDestroy, OnInit {
	static url = 'user-data';
	showRePassword = false;
	showPassword = false;
	passwordsMatching = false;
	isValidEmail = true;
	isValidName = true;
	isValidPassword = true;
	isValidRePassword = true;
	venue: Venue;
	email = 'email';
	password = 'password';
	rePassword = 'rePassword';
	firstName = 'firstName';
	lastName = 'lastName';
	gender = 'gender';
	agbChecked = 'agbChecked';
	day = 'day';
	month = 'month';
	year = 'year';
	phone = 'phone';
	emailSubs = 'emailSubs';

	loading = false;

	userDataForm: FormGroup | null = null;

	code = '';
	display: string = '';
	showTimerButton = true;

	public isEditableVenueSelection = true;
	protected readonly Gender = Gender;

	constructor(
		private translate: TranslateService,
		protected repository: RepositoryService,
		private snackbarCtrl: MatSnackBar,
		private navigationService: NavigationService,
		private modalCtrl: ModalController,
		private destroyRef: DestroyRef
	) {
		(window as any).repository = this.repository;
		if (repository.customer && !repository.customer.isAnonymous) {
			this.navigationService.account();
			return;
		}
		repository.customerAuth.pipe(takeUntilDestroyed(destroyRef)).subscribe(customerAuth => {
			this.userDataForm = this.createUserDataForm(customerAuth?.customer);
		});
	}
	ngOnInit() {
		this.userDataForm = this.createUserDataForm(this.repository.customer);
	}

	private hasMailingSubscription(customer: Customer | null): boolean {
		return Boolean(customer?.singleOptIn || customer?.doubleOptIn);
	}

	createUserDataForm(customer: Customer | null): FormGroup {
		const defaultPhone = getPhoneFormValue(customer?.phone);
		const birthDateParts = getBirthDateParts(customer?.birthDate);

		return new FormGroup({
			gender: new FormControl(customer?.gender ?? ''),
			firstName: new FormControl(customer?.firstName || customer?.name.split(' ')?.[0], [
				Validators.required,
				Validators.pattern(/^[^0-9]*$/),
			]),
			lastName: new FormControl(customer?.lastName || customer?.name.split(' ')?.[1], [
				Validators.required,
				Validators.pattern(/^[^0-9]*$/),
			]),
			email: new FormControl(customer?.email ?? '', [Validators.required, Validators.email]),
			day: new FormControl(birthDateParts?.day ?? '', [Validators.max(31)]),
			month: new FormControl(birthDateParts?.month ?? '', [Validators.max(12)]),
			year: new FormControl(birthDateParts?.year ?? '', [Validators.min(1900)]),
			phone: new FormControl(defaultPhone, [this._customPhoneValidatorForLibrary]),
			emailSubs: new FormControl(this.hasMailingSubscription(customer)),
		});
	}

	private _customPhoneValidatorForLibrary(control: AbstractControl) {
		if (!control.value?.phone) {
			return null;
		}
		return isPhoneFormValueValid(control.value) ? null : { invalidPhone: true };
	}

	isValid(controlName?: string): boolean {
		try {
			if (controlName) {
				return this.userDataForm.get(controlName)?.hasError('required') && this.userDataForm.get(controlName)?.touched;
			}
			return this.userDataForm.valid;
		} catch (e) {
			return false;
		}
	}

	isValidByPattern(controlName?: string): boolean {
		try {
			if (controlName) {
				return this.userDataForm.get(controlName)?.errors && !this.userDataForm.get(controlName)?.hasError('required');
			}
			return this.userDataForm.invalid;
		} catch (e) {
			return false;
		}
	}

	getControlValue(controlName?: string) {
		if (controlName) {
			return this.userDataForm?.get(controlName)?.value;
		}
		return '';
	}

	ngOnDestroy() {}

	async update() {
		try {
			this.loading = true;
			const hadMailingSubscription = this.hasMailingSubscription(this.repository.customer);

			const body = {
				...this.repository.customer,
				firstName: this.getControlValue('firstName'),
				lastName: this.getControlValue('lastName'),
				gender: this.getControlValue('gender'),

				// email: this.getControlValue('email'),
				preferredVenues: [],
				phone:
					this.getControlValue('phone')?.phone && this.getControlValue('phone')?.country
						? this.getControlValue('phone')?.country + this.getControlValue('phone')?.phone
						: null,
				birthDate: formatBirthDateForApi(
					this.getControlValue(this.year),
					this.getControlValue(this.month),
					this.getControlValue(this.day)
				),
				subscribedToMailing: this.getControlValue('emailSubs'),
			};
			if (body.subscribedToMailing && !hadMailingSubscription) {
				body.subscribedToMailingSource = 'app_account_area';
				body.subscribedToMailingAt = new Date().toISOString();
			}

			const response = await Api.patchCustomer(body);

			if (!!this.getControlValue('emailSubs') && !hadMailingSubscription) {
				this.snackbarCtrl.open(this.translate.instant('account.now_subscribed'), null, {
					duration: 3000,
				});
			}

			const mainUser = this.repository._customerAuth;
			mainUser.customer = {
				...mainUser.customer,
				...response.data,
				subscribedToMailing: body.subscribedToMailing,
				singleOptIn: body.subscribedToMailing,
				...(body.subscribedToMailing
					? {
							subscribedToMailingSource: body.subscribedToMailingSource,
							subscribedToMailingAt: body.subscribedToMailingAt,
						}
					: {}),
				...(body.subscribedToMailing ? {} : { doubleOptIn: undefined }),
			};
			this.repository.customerAuth.next(mainUser);
			this.loading = false;
			this.snackbarCtrl.open('Your account was updated', '', {
				duration: 4000,
			});
			// await InformationModalComponent.show(this.modalCtrl, {
			// 	title: this.translate.instant('sign_up.success'),
			// 	info: this.translate.instant('sign_up.updated'),
			// 	closeButton: this.translate.instant('add_points.close')
			// });
		} catch (e) {
			this.loading = false;

			if (e?.response?.data?.message) {
				this.snackbarCtrl.open(e.response.data.message, '', {
					duration: 4000,
				});
			} else {
				this.snackbarCtrl.open(this.translate.instant('account.error'), '', {
					duration: 2000,
				});
			}
		}
	}
	timer(minute: number) {
		let seconds: number = minute * 60;
		let textSec: any = '0';
		let statSec: number = 60;

		const prefix = minute < 60 ? '0' : '';

		const timer = setInterval(() => {
			this.showTimerButton = true;
			seconds--;
			if (statSec != 0) statSec--;
			else statSec = 59;

			if (statSec < 10) {
				textSec = '0' + statSec;
			} else textSec = statSec;

			this.display = `${prefix}${Math.floor(seconds / 60)}:${textSec}`;

			if (seconds == 0) {
				clearInterval(timer);
				this.display = '';
				this.showTimerButton = false;
			}
		}, 1000);
	}

	goBack() {
		if (this.repository._order === null) {
			this.navigationService.home();
		} else {
			this.navigationService.menu();
		}
	}
	gotoNextField(nextElement: IonInput) {
		nextElement.setFocus();
	}
	getErrorsForm(controlName) {
		try {
			if (controlName) {
				return this.userDataForm.get('phone')?.errors;
			}
		} catch (e) {}
		return null;
	}

	async deleteAccount() {
		const result = await InfoModalComponent.show(this.modalCtrl, {
			title: this.translate.instant('account.delete_account.header'),
			info: this.translate.instant('account.delete_account.msg'),
			mainButton: this.translate.instant('account.delete_account.yes'),
			closeButton: this.translate.instant('account.delete_account.no'),
		});
		if (result && this.repository?._customerAuth && this.repository?._customerAuth?.customer) {
			try {
				await Api.deleteAccount();
				this.snackbarCtrl.open(this.translate.instant('account.delete_account.success'), null, {
					duration: 3000,
				});
				this.repository.customerAuth.next(null);
				await this.navigationService.home();
			} catch (e) {
				this.loading = false;
				this.snackbarCtrl.open(this.translate.instant('account.delete_account.error'), null, {
					duration: 3000,
				});
			}
		}
	}
}
